import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { STAGES } from "@/crm/Pipeline";
import { AlertTriangle } from "lucide-react";

const SOURCES = ["Google", "Facebook", "Instagram", "Website", "QR Code", "Referral", "Walk-In", "Phone", "Direct Mail", "Existing Customer", "Customer Referral", "Other"];
const TYPES = ["Auto", "Home", "Auto + Home Bundle", "Renters", "Motorcycle", "Commercial / Business", "Commercial Auto", "Life Insurance", "Other"];

export default function Leads() {
  const [sp, setSp] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const nav = useNavigate();
  const f = {
    search: sp.get("search") || "", status: sp.get("status") || "", source: sp.get("source") || "",
    insurance_type: sp.get("insurance_type") || "", zip_code: sp.get("zip_code") || "",
    assigned_agent_id: sp.get("assigned_agent_id") || "", incomplete: sp.get("incomplete") || "",
  };

  useEffect(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/leads", { params }).then((r) => setLeads(r.data));
  }, [sp]);
  useEffect(() => { api.get("/users").then((r) => setAgents(r.data)).catch(() => {}); }, []);

  const upd = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const sel = "h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm";

  return (
    <div className="p-4 sm:p-6">
      <h1 className="font-display text-3xl font-bold mb-4">Leads <span className="font-mono text-lg text-slate-400">({leads.length})</span></h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <input className={`${sel} flex-1 min-w-[180px]`} placeholder="Search…" defaultValue={f.search} onKeyDown={(e) => e.key === "Enter" && upd("search", e.target.value)} data-testid="leads-search" />
        <select className={sel} value={f.status} onChange={(e) => upd("status", e.target.value)} data-testid="filter-status"><option value="">All Statuses</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select className={sel} value={f.insurance_type} onChange={(e) => upd("insurance_type", e.target.value)} data-testid="filter-type"><option value="">All Types</option>{TYPES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select className={sel} value={f.source} onChange={(e) => upd("source", e.target.value)} data-testid="filter-source"><option value="">All Sources</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select className={sel} value={f.assigned_agent_id} onChange={(e) => upd("assigned_agent_id", e.target.value)} data-testid="filter-agent"><option value="">All Agents</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
        <input className={`${sel} w-28`} placeholder="ZIP" defaultValue={f.zip_code} onKeyDown={(e) => e.key === "Enter" && upd("zip_code", e.target.value)} data-testid="filter-zip" />
        <button onClick={() => upd("incomplete", f.incomplete ? "" : "true")} className={`${sel} ${f.incomplete ? "bg-[#C5A059] text-[#0B1E36] font-bold" : ""}`} data-testid="filter-incomplete">Incomplete</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Progress</th><th className="px-4 py-3">Created</th></tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => nav(`/crm/leads/${l.id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 font-semibold">
                  {`${l.first_name || ""} ${l.last_name || ""}`.trim() || l.business_name || "Unnamed"}
                  {l.possible_duplicate_of && <AlertTriangle size={14} className="inline ml-1 text-[#C5A059]" title="Possible duplicate" />}
                </td>
                <td className="px-4 py-3 text-slate-600"><div>{l.phone}</div><div className="text-xs">{l.email}</div></td>
                <td className="px-4 py-3 text-slate-600">{(l.insurance_types || []).join(", ") || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs bg-slate-100 rounded px-2 py-0.5">{l.source}</span></td>
                <td className="px-4 py-3"><span className="text-xs font-semibold">{l.status}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{l.incomplete ? `${l.completion}%` : "100%"}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No leads match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
